import React, { useState, useMemo } from 'react';
import { Spinner, Row, Col, Card, Table, Badge, Pagination } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { Get } from '@app/utils/httpProvider';
import {
    DANHSACH_DUYET_NGOAINGU_API,
    ACCOUNT_API,
    ACCOUNT_AUTHINFO_API,
} from '@app/utils/apiUrl';

// ─── Helpers ────────────────────────────────────────────────────────────────

const parseTrangThai = (val) => {
    if (val === null || val === undefined) return -1;
    if (!isNaN(val)) return Number(val);
    const map = { NhapLieu: 0, ChoDuyet: 1, DaDuyet: 2, TuChoi: 3 };
    return map[String(val).trim()] ?? -1;
};

const parseXacNhanDat = (val) => {
    if (val === null || val === undefined) return null;
    const str = String(val).trim().toLowerCase();
    if (str === 'đạt' || str === 'dat' || str === 'true' || str === '1') return true;
    if (str === 'không đạt' || str === 'khong dat' || str === 'false' || str === '0') return false;
    return null;
};

const fetchAllItems = async (pageIndex, pageSize) => {
    return await Get(`${DANHSACH_DUYET_NGOAINGU_API.GET_THONGKE_NGOAINGU}?pageIndex=${pageIndex}&pageSize=${pageSize}`);
};

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, color, bg, subtitle }) => (
    <div
        style={{
            background: bg || 'linear-gradient(135deg,#667eea,#764ba2)',
            borderRadius: 16,
            padding: '24px 28px',
            color: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            height: '100%',
        }}
    >
        <div style={{ fontSize: 32, opacity: 0.85 }}>{icon}</div>
        <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.85, letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 42, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
        {subtitle && <div style={{ fontSize: 12, opacity: 0.75 }}>{subtitle}</div>}
    </div>
);

// ─── Mini Dual Bar ──────────────────────────────────────────────────────────

const DualBar = ({ labelA, countA, colorA, labelB, countB, colorB }) => {
    const total = countA + countB;
    const pctA = total ? Math.round((countA / total) * 100) : 0;
    const pctB = 100 - pctA;
    return (
        <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <div style={{
                    flex: pctA || 0.01, height: 12, background: colorA,
                    borderRadius: '6px 0 0 6px', transition: 'flex 0.4s',
                    minWidth: countA ? 4 : 0
                }} />
                <div style={{
                    flex: pctB || 0.01, height: 12, background: colorB,
                    borderRadius: '0 6px 6px 0', transition: 'flex 0.4s',
                    minWidth: countB ? 4 : 0
                }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#495057' }}>
                <span><span style={{ color: colorA, fontWeight: 600 }}>●</span> {labelA}: <strong>{countA}</strong> ({pctA}%)</span>
                <span><span style={{ color: colorB, fontWeight: 600 }}>●</span> {labelB}: <strong>{countB}</strong> ({pctB}%)</span>
            </div>
        </div>
    );
};

// ─── Mini Triple Bar ────────────────────────────────────────────────────────

const TripleBar = ({ labelA, countA, colorA, labelB, countB, colorB, labelC, countC, colorC }) => {
    const total = countA + countB + countC;
    const pctA = total ? Math.round((countA / total) * 100) : 0;
    const pctB = total ? Math.round((countB / total) * 100) : 0;
    const pctC = Math.max(0, 100 - pctA - pctB);
    return (
        <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <div style={{
                    flex: pctA || 0.01, height: 12, background: colorA,
                    borderRadius: '6px 0 0 6px', transition: 'flex 0.4s',
                    minWidth: countA ? 4 : 0
                }} />
                <div style={{
                    flex: pctB || 0.01, height: 12, background: colorB,
                    transition: 'flex 0.4s',
                    minWidth: countB ? 4 : 0
                }} />
                <div style={{
                    flex: pctC || 0.01, height: 12, background: colorC,
                    borderRadius: '0 6px 6px 0', transition: 'flex 0.4s',
                    minWidth: countC ? 4 : 0
                }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px 12px', fontSize: 13, color: '#495057' }}>
                <span><span style={{ color: colorA, fontWeight: 600 }}>●</span> {labelA}: <strong>{countA}</strong> ({pctA}%)</span>
                <span><span style={{ color: colorB, fontWeight: 600 }}>●</span> {labelB}: <strong>{countB}</strong> ({pctB}%)</span>
                <span><span style={{ color: colorC, fontWeight: 600 }}>●</span> {labelC}: <strong>{countC}</strong> ({pctC}%)</span>
            </div>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ThongKe = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const { data, isLoading, isError } = useQuery(
        ['thongKeNgoaiNgu', currentPage],
        () => fetchAllItems(currentPage, itemsPerPage),
        { cacheTime: 0, staleTime: 0, keepPreviousData: true }
    );

    const {
        soHoSo = 0,
        totalVBCC = 0,
        hoSoPhuHop = 0,
        hoSoKhongPhuHop = 0,
        hoSoChuaDuyet = 0,
        totalDonVi = 0,
        donViStats = []
    } = data || {};

    const totalPages = Math.ceil(totalDonVi / itemsPerPage);

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted fw-semibold">Đang tải dữ liệu thống kê...</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-5 text-danger">
                <i className="fas fa-exclamation-triangle fa-2x mb-2" />
                <p>Không thể tải dữ liệu thống kê. Vui lòng thử lại.</p>
            </div>
        );
    }

    return (
        <div className="py-3">
            {/* ── KPI Cards ── */}
            <Row className="g-3 mb-4">
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="👤"
                        label="Tổng số hồ sơ đã cập nhật"
                        value={soHoSo}
                        subtitle={`${totalVBCC} văn bằng / chứng chỉ`}
                        bg="linear-gradient(135deg,#667eea,#764ba2)"
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="✅"
                        label="Tổng số hồ sơ Phù hợp"
                        value={hoSoPhuHop}
                        subtitle="Có ít nhất 1 văn bằng/chứng chỉ Đạt yêu cầu"
                        bg="linear-gradient(135deg,#11998e,#38ef7d)"
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="❌"
                        label="Tổng số hồ sơ Không phù hợp"
                        value={hoSoKhongPhuHop}
                        subtitle="Tất cả văn bằng & chứng chỉ đều Không đạt"
                        bg="linear-gradient(135deg,#e53935,#e35d5b)"
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="⏳"
                        label="Tổng số hồ sơ Chưa duyệt"
                        value={hoSoChuaDuyet}
                        subtitle="Có ít nhất 1 văn bằng/chứng chỉ Chưa được duyệt"
                        bg="linear-gradient(135deg,#ff9800,#e65c00)"
                    />
                </Col>
            </Row>


            {/* ── Bảng theo đơn vị ── */}
            <Card className="shadow-sm border-0" style={{ borderRadius: 14 }}>
                <Card.Body>
                    <h6 className="fw-bold mb-3 text-dark">
                        <i className="fas fa-building me-2 text-primary" />
                        Số lượng người nộp hồ sơ theo đơn vị
                    </h6>
                    {donViStats.length === 0 ? (
                        <div className="text-center text-muted py-4">Không có dữ liệu</div>
                    ) : <div className="table-responsive">
                        <Table bordered hover className="align-middle mb-0" style={{ fontSize: '0.9rem' }}>
                            <thead className="table-light">
                                <tr className="text-nowrap fw-bold">
                                    <th className="text-center" style={{ width: 50, fontWeight: 700 }}>STT</th>
                                    <th className="text-start" style={{ fontWeight: 700 }}>Đơn vị</th>
                                    <th className="text-center" style={{ minWidth: 120, fontWeight: 700 }}>Số người nộp</th>
                                    <th className="text-center" style={{ minWidth: 140, fontWeight: 700 }}>Số người phù hợp</th>
                                    <th className="text-center" style={{ minWidth: 160, fontWeight: 700 }}>Số người Không phù hợp</th>
                                    <th className="text-center" style={{ minWidth: 160, fontWeight: 700 }}>Số người chưa duyệt</th>
                                </tr>
                            </thead>
                            <tbody>
                                {donViStats.map((row, idx) => {
                                    const stt = (currentPage - 1) * itemsPerPage + idx + 1;
                                    return (
                                        <tr key={row.tenDonVi} style={{ color: '#000' }}>
                                            <td className="text-center" style={{ color: '#000' }}>{stt}</td>
                                            <td style={{ color: '#000' }}>
                                                <span className="fw-semibold">{row.tenDonVi}</span>
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#667eea', borderRadius: 50 }}>
                                                    {row.soNguoi}
                                                </Badge>
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soPhuHop > 0 ? (
                                                    <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#11998e', borderRadius: 50 }}>
                                                        {row.soPhuHop}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soKhongPhuHop > 0 ? (
                                                    <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#e53935', borderRadius: 50 }}>
                                                        {row.soKhongPhuHop}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soChuaDuyet > 0 ? (
                                                    <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#ff9800', borderRadius: 50 }}>
                                                        {row.soChuaDuyet}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>

                        {/* Phân trang */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-end mt-3">
                                <Pagination className="custom-pagination">
                                    <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} />
                                    <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
                                    {[...Array(totalPages)].map((_, idx) => (
                                        <Pagination.Item
                                            key={idx + 1}
                                            active={idx + 1 === currentPage}
                                            onClick={() => setCurrentPage(idx + 1)}
                                        >
                                            {idx + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
                                    <Pagination.Last disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)} />
                                </Pagination>
                            </div>
                        )}
                    </div>}
                </Card.Body>
            </Card>

            <style>{`
                .thongke-section-title {
                    font-size: 0.8rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: #6c757d;
                    margin-bottom: 12px;
                }

                /* Pagination styling */
                .custom-pagination .page-link {
                    color: #0d6efd;
                    border-radius: 6px;
                    margin: 0 3px;
                    border: 1px solid #dee2e6;
                    padding: 6px 12px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                }
                .custom-pagination .page-item.active .page-link {
                    background-color: #0d6efd;
                    border-color: #0d6efd;
                    color: #fff;
                }
                .custom-pagination .page-item.disabled .page-link {
                    color: #6c757d;
                    background-color: #fff;
                    border-color: #dee2e6;
                }
                .custom-pagination .page-link:hover {
                    background-color: #e9ecef;
                    border-color: #dee2e6;
                }
            `}</style>
        </div>
    );
};

export default ThongKe;
